import { observer } from 'mobx-react-lite';

import { useEdgeResize } from '@/hooks/useEdgeResize';
import { cn } from '@/lib/utils';

import {
  MIN_TERMINAL_HEIGHT,
  sandboxTerminalPanelStore,
} from './sandbox-terminal-panel.store';

import type { ReactNode } from 'react';

interface TerminalDockProps {
  /**
   * When true, dock as an absolute bottom overlay over the surface behind it. When
   * false, render as an inline full-width flex row that shrinks the surface above.
   */
  overlay: boolean;
  /** Fixed width (overlay only); undefined = full width. */
  width?: number;
  /** Right-align instead of stretching edge to edge (overlay only). */
  alignRight?: boolean;
  /** Inset + rounded bottom corners, used when sitting under the file preview. */
  inset?: boolean;
  insetLeft?: boolean;
  /** Keep children mounted but take the dock out of layout (display: none). */
  hidden?: boolean;
  testId: string;
  children: ReactNode;
}

/**
 * Bottom-docked, resizable shell shared by the sandbox and local terminal panels.
 * Height lives in sandboxTerminalPanelStore (one dock, one height). Resizes by
 * dragging the top edge - mirrors the FilePreviewSidebar drag mechanics, including
 * blocking page pointer-events during the drag so the xterm canvas can't swallow
 * the mouseup.
 *
 * Must be an observer: it reads store.height directly, and the drag writes height
 * on every mousemove. Without observer it only re-rendered with its parent, i.e.
 * on mouseup.
 */
export const TerminalDock = observer(function TerminalDock({
  overlay,
  width,
  alignRight = false,
  inset = false,
  insetLeft = false,
  hidden = false,
  testId,
  children,
}: TerminalDockProps) {
  const height = sandboxTerminalPanelStore.height;
  // Bottom-docked panel: dragging the top edge upward makes it taller.
  const { isDragging, onResizeStart } = useEdgeResize({
    axis: 'y',
    min: MIN_TERMINAL_HEIGHT,
    max: () => window.innerHeight * 0.8,
    getStartSize: () => sandboxTerminalPanelStore.height,
    onResize: (next) => sandboxTerminalPanelStore.setHeight(next),
  });

  return (
    <div
      // the viewport cap only applies when pinned to the file preview's width;
      // a full-width dock must reach the column's right edge
      style={{
        height,
        maxHeight: '80%',
        width,
        maxWidth: width === undefined ? undefined : '80vw',
      }}
      className={cn(
        overlay ? 'absolute bottom-0 z-40' : 'relative w-full shrink-0',
        alignRight ? 'right-0' : overlay && 'inset-x-0',
        inset && 'pr-2 pb-2',
        insetLeft && 'pl-2',
        hidden && 'hidden'
      )}
      data-testid={testId}
      data-hidden={hidden || undefined}
    >
      <div
        className={cn(
          'relative h-full w-full border-t border-border bg-surface',
          inset && 'overflow-hidden rounded-b-[8px] border-x border-b'
        )}
      >
        <div
          onMouseDown={onResizeStart}
          className="group absolute left-0 right-0 top-0 z-10 flex h-1.5 cursor-ns-resize flex-col justify-start"
          role="separator"
          aria-orientation="horizontal"
          aria-label="Resize terminal"
        >
          <div
            className={cn(
              'h-0 w-full bg-border-strongest transition-all',
              'group-hover:h-0.5',
              isDragging && 'h-0.5'
            )}
          />
        </div>
        {children}
      </div>
    </div>
  );
});
